import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Container, Paper, Title, Text, Stack, Loader, Button, TextInput } from '@mantine/core';
import { IconCircleCheck, IconCircleX, IconClockExclamation } from '@tabler/icons-react';
import { authService } from '../../services/auth';
import { Logo } from '../../components/common';

type VerifyState = 'loading' | 'success' | 'expired' | 'error';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<VerifyState>(token ? 'loading' : 'error');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendError, setResendError] = useState('');

  useEffect(() => {
    if (!token) return;
    authService.verifyEmail(token)
      .then(() => setStatus('success'))
      .catch((err: Error) => {
        const msg = err.message?.toLowerCase() ?? '';
        if (msg.includes('expired') || msg.includes('invalid') || msg.includes('not found')) {
          setStatus('expired');
        } else if (msg.includes('already verified')) {
          setStatus('success');
        } else {
          setStatus('error');
        }
      });
  }, [token]);

  const handleResend = async () => {
    setResendError('');
    setResending(true);
    try {
      await authService.resendVerificationEmail(resendEmail.trim());
      setResendSent(true);
    } catch {
      setResendError('Failed to send. Please check the email address and try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <Container size={420} my={40}>
      <Stack align="center" mb="lg">
        <Logo size="lg" />
      </Stack>

      <Paper withBorder shadow="md" p={30} radius="md">
        <Stack align="center" gap="md">

          {status === 'loading' && (
            <>
              <Loader size="lg" />
              <Text>Verifying your email…</Text>
            </>
          )}

          {status === 'success' && (
            <>
              <IconCircleCheck size={48} color="var(--mantine-color-green-6)" />
              <Title order={3}>Email verified!</Title>
              <Text c="dimmed" ta="center">
                Your account is now active. You can sign in.
              </Text>
              <Button component={Link} to="/login" fullWidth>
                Sign in
              </Button>
            </>
          )}

          {status === 'expired' && (
            <>
              <IconClockExclamation size={48} color="var(--mantine-color-yellow-6)" />
              <Title order={3}>Link expired</Title>
              <Text c="dimmed" ta="center">
                This verification link has expired or already been used.
                Enter your email to get a new one.
              </Text>

              {resendSent ? (
                <Text c="green" ta="center" fw={500}>
                  Verification email sent — check your inbox.
                </Text>
              ) : (
                <Stack w="100%" gap="xs">
                  <TextInput
                    placeholder="Your email address"
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.currentTarget.value)}
                    error={resendError}
                  />
                  <Button
                    onClick={handleResend}
                    loading={resending}
                    disabled={!resendEmail.trim()}
                    fullWidth
                  >
                    Resend verification email
                  </Button>
                </Stack>
              )}

              <Button component={Link} to="/login" variant="subtle" fullWidth>
                Back to sign in
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <IconCircleX size={48} color="var(--mantine-color-red-6)" />
              <Title order={3}>Verification failed</Title>
              <Text c="dimmed" ta="center">
                This verification link is invalid. Please try registering again or contact support.
              </Text>
              <Button component={Link} to="/login" variant="default" fullWidth>
                Back to sign in
              </Button>
            </>
          )}

        </Stack>
      </Paper>
    </Container>
  );
}
